import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Palette, Ruler, Plus, Save, Trash2, Sparkles, Power, X } from 'lucide-react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import ConfigMenu from '../components/ConfigMenu';
import {
  fetchMaestrosTalleColor,
  setEnableVariantStock,
  cargarSugeridos,
  validateCurva,
  validateColor,
} from '../utils/talleColorMaestros';

const emptyCurva = { id: null, nombre: '', valoresTexto: '' };
const emptyColor = { id: null, nombre: '', hex: '#111111', codigo_corto: '' };

const Section = ({ icon: Icon, title, subtitle, children, action }) => (
  <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
    <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <div className="p-2 rounded-xl bg-blue-50 text-[#51A7FD] shrink-0"><Icon size={18} /></div>
        <div className="min-w-0">
          <h6 className="text-[11px] font-bold text-slate-800 uppercase tracking-widest">{title}</h6>
          {subtitle && <p className="text-[10px] text-slate-400 font-medium mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </section>
);

function ConfigTallesColores() {
  const navigate = useNavigate();
  const { profile, organization, refreshOrganization } = useAuth();
  const orgId = profile?.organization_id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [curvas, setCurvas] = useState([]);
  const [colores, setColores] = useState([]);
  const [curvaForm, setCurvaForm] = useState(emptyCurva);
  const [colorForm, setColorForm] = useState(emptyColor);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [{ curvas: c, colores: col }, orgRes] = await Promise.all([
        fetchMaestrosTalleColor(supabase, orgId),
        supabase.from('organizations').select('enable_variant_stock').eq('id', orgId).maybeSingle(),
      ]);
      setCurvas(c);
      setColores(col);
      setEnabled(Boolean(orgRes?.data?.enable_variant_stock));
    } catch (e) {
      console.error(e);
      toast.error('No se pudieron cargar talles y colores.');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const toggleEnabled = async () => {
    const next = !enabled;
    if (!next && !window.confirm('¿Desactivar talles y colores? Los productos que ya los usan siguen funcionando, pero no vas a poder configurar nuevos.')) return;
    setSaving(true);
    try {
      await setEnableVariantStock(supabase, orgId, next);
      setEnabled(next);
      await refreshOrganization?.();
      toast.success(next ? 'Talles y colores activados' : 'Talles y colores desactivados');
    } catch (e) {
      console.error(e);
      toast.error('No se pudo cambiar la configuración.');
    } finally {
      setSaving(false);
    }
  };

  const onCargarSugeridos = async () => {
    setSaving(true);
    try {
      const res = await cargarSugeridos(supabase, orgId, { curvas, colores });
      if (!res.curvas && !res.colores) toast('Ya tenés todos los sugeridos.');
      else toast.success(`Se agregaron ${res.curvas} curvas y ${res.colores} colores.`);
      await load();
    } catch (e) {
      console.error(e);
      toast.error('No se pudieron cargar los sugeridos.');
    } finally {
      setSaving(false);
    }
  };

  const saveCurva = async () => {
    const v = validateCurva(curvaForm);
    if (!v.ok) return toast.error(v.error);
    setSaving(true);
    try {
      const payload = { ...v.value, organization_id: orgId };
      const q = curvaForm.id
        ? supabase.from('talle_curvas').update(payload).eq('id', curvaForm.id)
        : supabase.from('talle_curvas').insert([payload]);
      const { error } = await q;
      if (error) throw error;
      toast.success(curvaForm.id ? 'Curva actualizada' : 'Curva creada');
      setCurvaForm(emptyCurva);
      await load();
    } catch (e) {
      console.error(e);
      toast.error(e?.code === '23505' ? 'Ya existe una curva con ese nombre.' : 'No se pudo guardar la curva.');
    } finally {
      setSaving(false);
    }
  };

  const deleteCurva = async (curva) => {
    if (!window.confirm(`¿Eliminar la curva "${curva.nombre}"? Los productos ya creados no cambian.`)) return;
    const { error } = await supabase.from('talle_curvas').delete().eq('id', curva.id);
    if (error) return toast.error('No se pudo eliminar la curva.');
    toast.success('Curva eliminada');
    load();
  };

  const saveColor = async () => {
    const v = validateColor(colorForm);
    if (!v.ok) return toast.error(v.error);
    setSaving(true);
    try {
      const payload = { ...v.value, organization_id: orgId };
      const q = colorForm.id
        ? supabase.from('colores').update(payload).eq('id', colorForm.id)
        : supabase.from('colores').insert([{ ...payload, orden: colores.length }]);
      const { error } = await q;
      if (error) throw error;
      toast.success(colorForm.id ? 'Color actualizado' : 'Color creado');
      setColorForm(emptyColor);
      await load();
    } catch (e) {
      console.error(e);
      toast.error(e?.code === '23505' ? 'Ya existe un color con ese nombre.' : 'No se pudo guardar el color.');
    } finally {
      setSaving(false);
    }
  };

  const toggleColorActivo = async (color) => {
    const { error } = await supabase.from('colores').update({ activo: !color.activo }).eq('id', color.id);
    if (error) return toast.error('No se pudo actualizar el color.');
    load();
  };

  const deleteColor = async (color) => {
    if (!window.confirm(`¿Eliminar el color "${color.nombre}"?`)) return;
    const { error } = await supabase.from('colores').delete().eq('id', color.id);
    if (error) {
      return toast.error(error.code === '23503'
        ? 'Este color ya se usa en productos: desactivalo en lugar de eliminarlo.'
        : 'No se pudo eliminar el color.');
    }
    toast.success('Color eliminado');
    load();
  };

  return (
    <div className="w-full min-h-screen bg-[#F8FAFC] font-sans text-slate-700 p-4 md:p-6 lg:p-8">
      <header className="flex items-center gap-4 mb-10 px-2">
        <button
          onClick={() => navigate('/sistema/config-productos-stock')}
          className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] transition-all shadow-sm"
          title="Volver"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-800 uppercase tracking-tight leading-none">Talles y colores</h1>
          <p className="text-[10px] font-medium text-slate-400 uppercase tracking-widest mt-2 italic">
            Curvas de talles, paleta de colores y stock por talle para indumentaria y calzado
          </p>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 max-w-[1600px] mx-auto">
        <aside className="lg:col-span-3">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-2"><ConfigMenu /></div>
        </aside>

        <main className="lg:col-span-9 space-y-6">
          <Section
            icon={Power}
            title="Activación"
            subtitle={organization?.name ? `Empresa: ${organization.name}` : undefined}
            action={(
              <button
                type="button"
                disabled={saving || loading}
                onClick={toggleEnabled}
                className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${enabled ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
              >
                {enabled ? 'Activado' : 'Activar'}
              </button>
            )}
          >
            <p className="text-[12px] text-slate-600 leading-relaxed">
              Con talles y colores activados, cada producto puede tener una matriz de talle × color con
              <b> stock propio por talle y sucursal</b>, código de barras y SKU propios y, si hace falta, precio propio
              (por ejemplo, un XXL más caro). El POS muestra un selector de color y talle con el stock de la sucursal.
              Los productos que no uses con talles siguen funcionando exactamente igual.
            </p>
            <button
              type="button"
              onClick={onCargarSugeridos}
              disabled={saving || loading}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#51A7FD]/10 text-[#2563EB] text-[10px] font-black uppercase tracking-widest hover:bg-[#51A7FD]/20"
            >
              <Sparkles size={14} /> Cargar curvas y colores sugeridos
            </button>
          </Section>

          <Section icon={Ruler} title="Curvas de talles" subtitle="Se eligen al crear la matriz de un producto. El orden es el que se muestra en el POS.">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-3 items-end mb-5">
              <label className="block">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Nombre</span>
                <input
                  className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2 text-sm"
                  placeholder="Adulto XS-XXL"
                  value={curvaForm.nombre}
                  onChange={(e) => setCurvaForm((f) => ({ ...f, nombre: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Talles, en orden</span>
                <input
                  className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2 text-sm"
                  placeholder="XS, S, M, L, XL, XXL"
                  value={curvaForm.valoresTexto}
                  onChange={(e) => setCurvaForm((f) => ({ ...f, valoresTexto: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveCurva(); }}
                />
              </label>
              <div className="flex gap-2">
                {curvaForm.id && (
                  <button type="button" onClick={() => setCurvaForm(emptyCurva)} className="p-2.5 rounded-xl border border-slate-200 text-slate-400" title="Cancelar">
                    <X size={16} />
                  </button>
                )}
                <button type="button" onClick={saveCurva} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#4f46e5] text-white text-[10px] font-black uppercase tracking-widest">
                  {curvaForm.id ? <Save size={14} /> : <Plus size={14} />} {curvaForm.id ? 'Guardar' : 'Agregar'}
                </button>
              </div>
            </div>

            {loading ? (
              <p className="text-sm text-slate-400">Cargando…</p>
            ) : curvas.length === 0 ? (
              <p className="text-sm text-slate-400">Todavía no hay curvas. Cargá las sugeridas o creá la tuya.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {curvas.map((c) => (
                  <li key={c.id} className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800">{c.nombre}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(c.valores || []).map((t) => (
                          <span key={t} className="px-2 py-0.5 rounded-md bg-slate-100 text-[11px] font-bold text-slate-600">{t}</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button type="button" className="text-[10px] font-bold uppercase text-[#4f46e5]"
                        onClick={() => setCurvaForm({ id: c.id, nombre: c.nombre, valoresTexto: (c.valores || []).join(', ') })}>Editar</button>
                      <button type="button" className="text-slate-300 hover:text-red-500" onClick={() => deleteCurva(c)} title="Eliminar"><Trash2 size={16} /></button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section icon={Palette} title="Colores" subtitle="La muestra de color se ve en el POS y en la tienda online. El código corto arma el SKU (REM01-NEG-M).">
            <div className="grid grid-cols-1 md:grid-cols-[2fr_auto_1fr_auto] gap-3 items-end mb-5">
              <label className="block">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Nombre</span>
                <input className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2 text-sm" placeholder="Negro"
                  value={colorForm.nombre} onChange={(e) => setColorForm((f) => ({ ...f, nombre: e.target.value }))} />
              </label>
              <label className="block">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Muestra</span>
                <input type="color" className="mt-1 h-[38px] w-14 border border-slate-200 rounded-xl p-1 bg-white"
                  value={colorForm.hex || '#111111'} onChange={(e) => setColorForm((f) => ({ ...f, hex: e.target.value }))} />
              </label>
              <label className="block">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Código corto</span>
                <input className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2 text-sm uppercase" placeholder="NEG" maxLength={6}
                  value={colorForm.codigo_corto} onChange={(e) => setColorForm((f) => ({ ...f, codigo_corto: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveColor(); }} />
              </label>
              <div className="flex gap-2">
                {colorForm.id && (
                  <button type="button" onClick={() => setColorForm(emptyColor)} className="p-2.5 rounded-xl border border-slate-200 text-slate-400" title="Cancelar"><X size={16} /></button>
                )}
                <button type="button" onClick={saveColor} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#4f46e5] text-white text-[10px] font-black uppercase tracking-widest">
                  {colorForm.id ? <Save size={14} /> : <Plus size={14} />} {colorForm.id ? 'Guardar' : 'Agregar'}
                </button>
              </div>
            </div>

            {loading ? (
              <p className="text-sm text-slate-400">Cargando…</p>
            ) : colores.length === 0 ? (
              <p className="text-sm text-slate-400">Todavía no hay colores. Cargá los sugeridos o creá los tuyos.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
                {colores.map((c) => (
                  <div key={c.id} className={`flex items-center gap-3 p-3 rounded-xl border ${c.activo ? 'border-slate-200' : 'border-dashed border-slate-200 opacity-50'}`}>
                    <span className="w-8 h-8 rounded-full border border-slate-300 shrink-0" style={{ background: c.hex || 'transparent' }} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-800 truncate">{c.nombre}</p>
                      <p className="text-[10px] text-slate-400 font-bold">{c.codigo_corto || '—'}{!c.activo && ' · inactivo'}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <button type="button" className="text-[9px] font-bold uppercase text-[#4f46e5]"
                        onClick={() => setColorForm({ id: c.id, nombre: c.nombre, hex: c.hex || '#111111', codigo_corto: c.codigo_corto || '' })}>Editar</button>
                      <button type="button" className="text-[9px] font-bold uppercase text-slate-400" onClick={() => toggleColorActivo(c)}>
                        {c.activo ? 'Desactivar' : 'Activar'}
                      </button>
                      <button type="button" className="text-slate-300 hover:text-red-500" onClick={() => deleteColor(c)} title="Eliminar"><Trash2 size={13} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </main>
      </div>
    </div>
  );
}

export default ConfigTallesColores;
